import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { InfoBanner } from '@repo/design-system/components/ui/info-banner';
import { Modal, type ModalProps } from '@repo/design-system/components/ui/modal';
import { Type } from '@repo/design-system/components/ui/type';
import { overlayBorderStyles } from '@repo/design-system/custom-styles';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css, cx } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import {
    selectCurrentMapInfo,
    selectGroupedCategories,
    selectMapConfig,
    selectMapName,
    selectSteamId,
    selectTitleName,
} from '@repo/store/maps/slices/mapData';
import { getSteamImageUrl, getWandSteamCdnImageUrl } from '@repo/utility/image-urls';
import { useEffect, useState } from 'react';
import { MapsAuthModal } from '../components/maps-auth-modal';
import { MapDiscoveryProgress } from '../map-discovery-progress';
import { MapShareModal } from '../map-share-modal/map-share-modal';
import { useMapShareHandlers } from '../utils/use-map-share-handlers';

const ONE_WEEK_IN_MS = 1000 * 60 * 60 * 24 * 7;

export const MapDetailsModal = ({ isOpen, onOpenChange }: ModalProps) => {
    const mapName = useAppSelector(selectMapName);
    const gameTitle = useAppSelector(selectTitleName);
    const mapConfig = useAppSelector(selectMapConfig);
    const currentMapInfo = useAppSelector(selectCurrentMapInfo);
    const steamId = useAppSelector(selectSteamId);
    const groupedCategories = useAppSelector(selectGroupedCategories);
    const isLoggedIn = useAppSelector(state => state.auth.isAuthenticated);
    const hostType = useHostType();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const analytics = useAnalytics();
    const { t } = useT(['game_map']);

    const heroImageUrl = steamId ? getSteamImageUrl(steamId, 'hero') : null;
    const capsuleImageUrl = steamId ? getWandSteamCdnImageUrl(steamId, 'capsule') : null;

    const wasMapUpdatedRecently =
        mapConfig?.lastUpdated && new Date(mapConfig.lastUpdated) > new Date(Date.now() - ONE_WEEK_IN_MS);
    const isMapInActiveDevelopment = currentMapInfo?.isInActiveDevelopment ?? mapConfig?.isInActiveDevelopment ?? false;
    const shouldShowActiveDevelopmentBanner = wasMapUpdatedRecently || isMapInActiveDevelopment;

    const totalPins =
        groupedCategories?.reduce(
            (sum, category) => sum + category.subcategories.reduce((catSum, sub) => catSum + sub.locationCount, 0),
            0,
        ) ?? 0;

    const updatedDate = mapConfig?.lastUpdated
        ? new Date(mapConfig.lastUpdated).toLocaleDateString(undefined, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
          })
        : '-';

    const handleMapDiscoveryProgressClick = () => {
        if (!isLoggedIn) {
            setIsLoginModalOpen(true);
        }
    };

    const { handleRedditShareClick, handleDiscordShareClick, handleShareXClick } = useMapShareHandlers({
        mapName: mapName || '',
        gameTitle: gameTitle || '',
        titleSlug: mapConfig?.titleSlug || '',
        mapSlug: currentMapInfo?.slug || '',
    });

    useEffect(() => {
        if (isOpen && analytics && currentMapInfo?.externalId && hostType) {
            analytics.event('game_map_details_modal_open', {
                mapId: currentMapInfo.externalId,
                platform: hostType,
            });
        }
    }, [analytics, currentMapInfo?.externalId, hostType, isOpen]);

    return (
        <>
            <Modal
                variant="transparent"
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="sm"
                title={null}
                className={cx(
                    overlayBorderStyles(),
                    css({
                        background: 'wemod.elevated.100',
                        overflow: 'hidden',
                        _after: {
                            zIndex: -1,
                        },
                    }),
                )}
            >
                {heroImageUrl && (
                    <>
                        <img
                            loading="lazy"
                            alt=""
                            src={heroImageUrl}
                            className={css({
                                width: 'full',
                                height: '[120px]',
                                position: 'absolute',
                                zIndex: -1,
                                top: '0',
                                left: '0',
                                objectFit: 'cover',
                            })}
                        />
                        {/* Overlay gradient blending hero image into content */}
                        <div
                            className={css({
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                right: '0',
                                height: '[120px]',
                                background:
                                    '[linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, var(--colors-wemod-elevated-40) 50%, var(--colors-wemod-elevated-100) 100%)]',
                                pointerEvents: 'none',
                                zIndex: 0,
                            })}
                            aria-hidden
                        />
                    </>
                )}

                <div className={css({ position: 'relative', zIndex: 1, isolation: 'isolate' })}>
                    <FlexColumn gap="16">
                        <FlexRow
                            align="center"
                            gap="12"
                            className={css.raw({ ...(heroImageUrl ? { marginTop: '60' } : {}) })}
                        >
                            {capsuleImageUrl && (
                                <div
                                    className={cx(
                                        overlayBorderStyles(),
                                        css({
                                            width: '[40px]',
                                            flexShrink: '0',
                                            borderRadius: '6',
                                        }),
                                    )}
                                >
                                    <img
                                        loading="lazy"
                                        alt=""
                                        src={capsuleImageUrl}
                                        className={css({
                                            borderRadius: '6',
                                        })}
                                    />
                                </div>
                            )}
                            <FlexColumn>
                                <Type variant="header-l" color="white">
                                    {mapName}
                                </Type>
                                <Type variant="body-s" color="text.secondary">
                                    {gameTitle}
                                </Type>
                            </FlexColumn>
                        </FlexRow>
                        <FlexRow justify="start" width="full">
                            <FlexColumn className={css.raw({ flexBasis: '[45%]' })}>
                                <Type variant="caption" color="text.secondary">
                                    {t($ => $.game_map_details.total_map_pins)}
                                </Type>
                                <Type variant="header-s" color="white">
                                    {totalPins}
                                </Type>
                            </FlexColumn>
                            <FlexColumn className={css.raw({ flexBasis: '[45%]' })}>
                                <Type variant="caption" color="text.secondary">
                                    {t($ => $.game_map_details.updated)}
                                </Type>
                                <Type variant="header-s" color="white">
                                    {updatedDate}
                                </Type>
                            </FlexColumn>
                        </FlexRow>

                        <FlexColumn gap="8" width="full">
                            <MapDiscoveryProgress onClick={!isLoggedIn ? handleMapDiscoveryProgressClick : undefined} />

                            {shouldShowActiveDevelopmentBanner && (
                                <InfoBanner icon="info" variant="info">
                                    <Type variant="body-xs" color="current">
                                        {t($ => $.game_map_details.map_being_worked_on)}
                                    </Type>
                                </InfoBanner>
                            )}
                        </FlexColumn>

                        <FlexRow justify="center" gap="8" align="center" width="full">
                            <IconButton
                                onClick={handleRedditShareClick}
                                color="secondary"
                                size="lg"
                                aria-label={t($ => $.reddit)}
                            >
                                <svg
                                    aria-hidden="true"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 23 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M14.4646 2.92824C14.7123 3.97835 15.6553 4.76063 16.7813 4.76063C18.0958 4.76063 19.1616 3.6949 19.1616 2.38031C19.1616 1.06573 18.0958 0 16.7813 0C15.6318 0 14.6732 0.814641 14.45 1.89823C12.525 2.10468 11.0219 3.73731 11.0219 5.71588C11.0219 5.72034 11.0219 5.72369 11.0219 5.72816C8.92834 5.81631 7.01672 6.41223 5.49903 7.35297C4.93548 6.91664 4.22797 6.65662 3.4602 6.65662C1.61777 6.65662 0.124634 8.14976 0.124634 9.99219C0.124634 11.3291 0.91026 12.4807 2.04518 13.0131C2.15566 16.8854 6.37505 20 11.5653 20C16.7556 20 20.9806 16.882 21.0855 13.0064C22.2115 12.4707 22.9904 11.3224 22.9904 9.9933C22.9904 8.15088 21.4972 6.65774 19.6548 6.65774C18.8904 6.65774 18.1862 6.91552 17.6238 7.34851C16.0927 6.40107 14.1599 5.80516 12.0452 5.72592C12.0452 5.72258 12.0452 5.72034 12.0452 5.717C12.0452 4.29974 13.0986 3.12354 14.4646 2.93048V2.92824ZM5.36512 12.1091C5.42092 10.8995 6.2244 9.97099 7.15845 9.97099C8.09249 9.97099 8.8067 10.9519 8.7509 12.1616C8.69511 13.3713 7.99764 13.811 7.06248 13.811C6.12731 13.811 5.30932 13.3188 5.36512 12.1091ZM15.9733 9.97099C16.9085 9.97099 17.712 10.8995 17.7666 12.1091C17.8224 13.3188 17.0033 13.811 16.0693 13.811C15.1352 13.811 14.4367 13.3724 14.3809 12.1616C14.3251 10.9519 15.0381 9.97099 15.9733 9.97099ZM14.8618 14.9079C15.037 14.9258 15.1486 15.1077 15.0806 15.2706C14.5058 16.6443 13.1488 17.6096 11.5653 17.6096C9.98179 17.6096 8.62592 16.6443 8.05009 15.2706C7.98202 15.1077 8.09361 14.9258 8.26881 14.9079C9.29549 14.8042 10.4059 14.7472 11.5653 14.7472C12.7248 14.7472 13.834 14.8042 14.8618 14.9079Z"
                                        fill="white"
                                    />
                                </svg>
                            </IconButton>
                            <IconButton
                                onClick={handleDiscordShareClick}
                                color="secondary"
                                size="lg"
                                aria-label={t($ => $.discord)}
                            >
                                <svg
                                    aria-hidden="true"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 21 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M17.6919 2.58728C17.6919 2.58728 17.6919 2.58728 17.6575 2.58728C16.3509 1.96837 14.9756 1.55577 13.5658 1.31509C13.5315 1.2807 13.4971 1.31509 13.4971 1.31509C13.2908 1.65892 13.1189 2.03714 12.9813 2.38098C11.4341 2.14029 9.8868 2.14029 8.33955 2.38098C8.20201 2.03714 8.03009 1.65892 7.82379 1.31509C7.82379 1.31509 7.78941 1.2807 7.75503 1.31509C6.3453 1.55577 4.96996 1.96837 3.66339 2.58728C3.629 2.58728 3.629 2.58728 3.629 2.58728C1.01585 6.507 0.2938 10.3236 0.637635 14.1058C0.637635 14.1058 0.637635 14.1401 0.672019 14.1401C2.18489 15.2748 3.90407 16.1344 5.7264 16.6845C5.7264 16.7189 5.76078 16.6845 5.79516 16.6845C6.17338 16.1344 6.51722 15.5842 6.82667 14.9997C6.82667 14.9653 6.82667 14.931 6.79229 14.8966C6.24215 14.6903 5.69201 14.4496 5.21064 14.1401C5.17626 14.1401 5.14188 14.0714 5.21064 14.037C5.3138 13.9682 5.41695 13.8995 5.5201 13.7963C5.5201 13.7963 5.55448 13.7963 5.58886 13.7963C8.88968 15.3092 12.4656 15.3092 15.732 13.7963C15.7664 13.7963 15.7664 13.7963 15.8008 13.7963C15.9039 13.8995 16.0071 13.9682 16.1102 14.037C16.1446 14.0714 16.1446 14.1401 16.1102 14.1401C15.5945 14.4496 15.0787 14.6903 14.5286 14.8966C14.4942 14.931 14.4942 14.9653 14.4942 14.9997C14.8036 15.5842 15.1475 16.1344 15.5257 16.6845C15.5601 16.6845 15.5601 16.7189 15.5945 16.6845C17.4168 16.1344 19.136 15.2748 20.6488 14.1401C20.6832 14.1401 20.6832 14.1058 20.6832 14.1058C21.0958 9.73905 19.9612 5.95686 17.6919 2.58728ZM7.30804 11.8021C6.31092 11.8021 5.48571 10.8737 5.48571 9.77343C5.48571 8.63878 6.31092 7.74481 7.30804 7.74481C8.33955 7.74481 9.13037 8.63878 9.13037 9.77343C9.13037 10.8737 8.30516 11.8021 7.30804 11.8021ZM14.0128 11.8021C13.0157 11.8021 12.2249 10.8737 12.2249 9.77343C12.2249 8.63878 13.0157 7.74481 14.0128 7.74481C15.0443 7.74481 15.8695 8.63878 15.8352 9.77343C15.8352 10.8737 15.0443 11.8021 14.0128 11.8021Z"
                                        fill="white"
                                    />
                                </svg>
                            </IconButton>
                            <IconButton
                                onClick={handleShareXClick}
                                color="secondary"
                                size="lg"
                                aria-label={t($ => $.x)}
                            >
                                <svg
                                    aria-hidden="true"
                                    width="20"
                                    height="15"
                                    viewBox="0 0 15 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1299_9628)">
                                        <path
                                            d="M8.86023 5.925L13.9574 0H12.7495L8.32367 5.14459L4.78876 0H0.71167L6.05714 7.77954L0.71167 13.9928H1.91959L6.59339 8.55995L10.3265 13.9928H14.4036L8.85993 5.925H8.86023ZM7.20581 7.84808L6.6642 7.07341L2.35483 0.909309H4.21013L7.68785 5.88394L8.22945 6.6586L12.7501 13.1249H10.8948L7.20581 7.84837V7.84808Z"
                                            fill="white"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1299_9628">
                                            <rect
                                                width="13.6919"
                                                height="14"
                                                fill="white"
                                                transform="translate(0.711731)"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </IconButton>
                            <Button
                                onClick={() => setIsShareModalOpen(true)}
                                color="secondary"
                                size="lg"
                                aria-label={t($ => $.share)}
                                className={css({
                                    flex: '1',
                                })}
                            >
                                <Icon icon="share" decorative />
                                {t($ => $.share)}
                            </Button>
                        </FlexRow>
                    </FlexColumn>
                </div>
            </Modal>
            <MapShareModal isOpen={isShareModalOpen} onOpenChange={({ open }) => setIsShareModalOpen(open)} />
            <MapsAuthModal
                isOpen={isLoginModalOpen}
                onOpenChange={details => setIsLoginModalOpen(details.open)}
                trigger="discovery-progress"
                size="sm"
            />
        </>
    );
};
